import type { LoggerPort } from "../ports.ts";
import type { GameLogContext, LogEntry, LogLevel } from "./types.ts";

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
};

export interface LoggingEnvironment {
  dev: boolean;
  vitest: boolean;
}

let loggingEnv: LoggingEnvironment = { dev: false, vitest: false };
let minimumDevLevel: LogLevel = "info";
let channelAllowlist: ReadonlySet<string> | null = null;
const recentLogs: LogEntry[] = [];
let recentLogsCapacity = 100;
let resolveLogContext: (() => GameLogContext | undefined) | null = null;

/** Hosts call once at boot with `import.meta.env.DEV` / `VITEST`. */
export function configureLoggingEnvironment(env: LoggingEnvironment): void {
  loggingEnv = env;
}

export function getLoggingEnvironment(): LoggingEnvironment {
  return loggingEnv;
}

export function setDevMinimumLogLevel(level: LogLevel): void {
  minimumDevLevel = level;
}

export function getDevMinimumLogLevel(): LogLevel {
  return minimumDevLevel;
}

export function meetsMinimumLogLevel(level: LogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[minimumDevLevel];
}

export function setChannelAllowlist(channels: readonly string[] | null): void {
  channelAllowlist = channels === null ? null : new Set(channels);
}

function passesChannelAllowlist(channel: string): boolean {
  return channelAllowlist === null || channelAllowlist.has(channel);
}

export function clearRecentLogs(): void {
  recentLogs.length = 0;
}

export function getRecentLogs(): readonly LogEntry[] {
  return recentLogs.slice();
}

export function setRecentLogsCapacity(capacity: number): void {
  recentLogsCapacity = Math.max(1, capacity);
  while (recentLogs.length > recentLogsCapacity) {
    recentLogs.shift();
  }
}

/** Ring buffer is DEV-only; production builds do not retain log entries. */
export function shouldBufferForEnvironment(env: { dev: boolean }): boolean {
  return env.dev;
}

function pushRecentLog(entry: LogEntry): void {
  if (!shouldBufferForEnvironment({ dev: loggingEnv.dev })) {
    return;
  }

  recentLogs.push(entry);
  while (recentLogs.length > recentLogsCapacity) {
    recentLogs.shift();
  }
}

export function setLogContextResolver(
  resolver: (() => GameLogContext | undefined) | null,
): void {
  resolveLogContext = resolver;
}

export function mergeLogContext(
  context?: GameLogContext | Record<string, unknown>,
): GameLogContext | undefined {
  const resolved = resolveLogContext?.();
  const explicit = asGameLogContext(context);
  if (!resolved && !explicit) {
    return undefined;
  }

  return {
    ...resolved,
    ...explicit,
    sessionId: explicit?.sessionId ?? resolved?.sessionId,
    turn: explicit?.turn ?? resolved?.turn,
    phase: explicit?.phase ?? resolved?.phase,
  };
}

function asGameLogContext(
  context?: GameLogContext | Record<string, unknown> | unknown,
): GameLogContext | undefined {
  if (!context || typeof context !== "object") {
    return undefined;
  }
  const record = context as Record<string, unknown>;
  const merged: GameLogContext = {};
  if (typeof record.sessionId === "string") {
    merged.sessionId = record.sessionId;
  }
  if (typeof record.turn === "number") {
    merged.turn = record.turn;
  }
  if (typeof record.phase === "string") {
    merged.phase = record.phase as GameLogContext["phase"];
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

export function formatLogPrefix(channel: string): string {
  return `[dawn:${channel}]`;
}

export function shouldEmitForEnvironment(
  level: LogLevel,
  env: LoggingEnvironment,
): boolean {
  if (level === "warn" || level === "error") {
    return true;
  }

  return env.dev && !env.vitest;
}

export function shouldEmit(level: LogLevel): boolean {
  return shouldEmitForEnvironment(level, loggingEnv);
}

export function buildLogEntry(
  level: LogLevel,
  channel: string,
  event: string,
  context?: unknown,
  data?: Record<string, unknown>,
): LogEntry {
  const merged = mergeLogContext(
    context as GameLogContext | Record<string, unknown> | undefined,
  );

  return {
    ts: Date.now(),
    level,
    channel,
    event,
    ...(merged?.sessionId !== undefined ? { sessionId: merged.sessionId } : {}),
    ...(merged?.turn !== undefined ? { turn: merged.turn } : {}),
    ...(merged?.phase !== undefined ? { phase: merged.phase } : {}),
    ...(data !== undefined && Object.keys(data).length > 0 ? { data } : {}),
  };
}

/**
 * Debug/trace/info use `console.info` so they appear under the default DevTools
 * "Info" filter (unlike `console.debug`, which browsers often hide).
 */
function writeToConsole(entry: LogEntry): void {
  const prefix = formatLogPrefix(entry.channel);
  const payload: Record<string, unknown> = {
    event: entry.event,
    ...(entry.sessionId !== undefined ? { sessionId: entry.sessionId } : {}),
    ...(entry.turn !== undefined ? { turn: entry.turn } : {}),
    ...(entry.phase !== undefined ? { phase: entry.phase } : {}),
    ...(entry.data !== undefined ? { ...entry.data } : {}),
  };

  switch (entry.level) {
    case "error":
      console.error(prefix, payload);
      break;
    case "warn":
      console.warn(prefix, payload);
      break;
    case "trace":
    case "debug":
    case "info":
    default:
      console.info(prefix, payload);
      break;
  }
}

export function log(
  level: LogLevel,
  channel: string,
  event: string,
  context?: unknown,
  data?: Record<string, unknown>,
): LogEntry | null {
  if (
    !shouldEmit(level) ||
    !meetsMinimumLogLevel(level) ||
    !passesChannelAllowlist(channel)
  ) {
    return null;
  }

  const entry = buildLogEntry(level, channel, event, context, data);
  pushRecentLog(entry);
  writeToConsole(entry);
  return entry;
}

export const gameLogger: LoggerPort & {
  trace(
    topic: string,
    message: string,
    context?: unknown,
    data?: Record<string, unknown>,
  ): LogEntry | null;
  error(
    topic: string,
    message: string,
    context?: unknown,
    data?: Record<string, unknown>,
  ): LogEntry | null;
} = {
  trace(topic, message, context, data) {
    return log("trace", topic, message, context, data);
  },

  debug(topic, message, context, data) {
    return log("debug", topic, message, context, data);
  },

  info(topic, message, context, data) {
    return log("info", topic, message, context, data);
  },

  warn(topic, message, context, data) {
    return log("warn", topic, message, context, data);
  },

  error(topic, message, context, data) {
    return log("error", topic, message, context, data);
  },
};
